import {
  getPeriodRange,
  type PeriodPresetKey,
  type PeriodRange,
} from '@/utils/format';

const STORAGE_KEY = 'jgi.financeContext.v2';
const SAVED_VIEWS_KEY = 'jgi.financeSavedViews.v1';

export type FinanceContextState = {
  companyId: number | null;
  from: string;
  to: string;
  periodPreset: PeriodPresetKey;
};

export type FinanceSavedView = {
  id: string;
  name: string;
  scope: 'dashboard' | 'reconciliation' | 'journals';
  companyId: number | null;
  from: string;
  to: string;
  periodPreset: PeriodPresetKey;
  extra?: Record<string, string | number | boolean | null>;
  createdAt: string;
};

const companyId = ref<number | null>(null);
const from = ref(getPeriodRange('this_year').from);
const to = ref(getPeriodRange('this_year').to);
const periodPreset = ref<PeriodPresetKey>('this_year');
const hydrated = ref(false);
let persistBound = false;

function readStorage(): FinanceContextState | null {
  if (!import.meta.client) return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as FinanceContextState;
    if (!parsed?.from || !parsed?.to) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeStorage() {
  if (!import.meta.client) return;
  try {
    const payload: FinanceContextState = {
      companyId: companyId.value,
      from: from.value,
      to: to.value,
      periodPreset: periodPreset.value,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* ignore */
  }
}

function bindPersist() {
  if (persistBound) return;
  persistBound = true;
  watch([companyId, from, to, periodPreset], () => {
    if (!hydrated.value) return;
    writeStorage();
  });
}

function hydrate() {
  if (hydrated.value) return;
  const saved = readStorage();
  if (saved) {
    companyId.value = saved.companyId ?? null;
    from.value = String(saved.from).slice(0, 10);
    to.value = String(saved.to).slice(0, 10);
    periodPreset.value = (saved.periodPreset || 'this_year') as PeriodPresetKey;
  }
  hydrated.value = true;
}

export function useFinanceContext() {
  bindPersist();
  if (import.meta.client && !hydrated.value) hydrate();

  const rangeLabel = computed(() => `${from.value} → ${to.value}`);

  function setCompany(id: number | null) {
    companyId.value = id;
  }

  function setRange(next: PeriodRange, preset: PeriodPresetKey = 'custom') {
    from.value = next.from;
    to.value = next.to;
    periodPreset.value = preset;
  }

  function setPeriodPreset(key: PeriodPresetKey) {
    if (!key || key === 'custom') {
      periodPreset.value = 'custom';
      return;
    }
    const range = getPeriodRange(key);
    setRange(range, key);
  }

  function applySnapshot(snap: Partial<FinanceContextState>) {
    if (snap.companyId !== undefined) companyId.value = snap.companyId;
    if (snap.from) from.value = String(snap.from).slice(0, 10);
    if (snap.to) to.value = String(snap.to).slice(0, 10);
    if (snap.periodPreset) periodPreset.value = snap.periodPreset;
  }

  function snapshot(): FinanceContextState {
    return {
      companyId: companyId.value,
      from: from.value,
      to: to.value,
      periodPreset: periodPreset.value,
    };
  }

  /** Push context into page-local refs without fighting first paint. */
  function syncInto(local: {
    companyId?: Ref<number | null>;
    from?: Ref<string>;
    to?: Ref<string>;
    periodPreset?: Ref<PeriodPresetKey>;
  }) {
    if (local.companyId) local.companyId.value = companyId.value;
    if (local.from) local.from.value = from.value;
    if (local.to) local.to.value = to.value;
    if (local.periodPreset) local.periodPreset.value = periodPreset.value;
  }

  return {
    companyId,
    from,
    to,
    periodPreset,
    rangeLabel,
    hydrated,
    setCompany,
    setRange,
    setPeriodPreset,
    applySnapshot,
    snapshot,
    syncInto,
  };
}

export function loadFinanceSavedViews(): FinanceSavedView[] {
  if (!import.meta.client) return [];
  try {
    const raw = localStorage.getItem(SAVED_VIEWS_KEY);
    const list = raw ? (JSON.parse(raw) as FinanceSavedView[]) : [];
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
}

export function saveFinanceSavedView(view: Omit<FinanceSavedView, 'id' | 'createdAt'> & { id?: string }) {
  const list = loadFinanceSavedViews();
  const next: FinanceSavedView = {
    id: view.id || `v_${Date.now()}`,
    name: view.name,
    scope: view.scope,
    companyId: view.companyId,
    from: view.from,
    to: view.to,
    periodPreset: view.periodPreset,
    extra: view.extra,
    createdAt: new Date().toISOString(),
  };
  const merged = [next, ...list.filter((v) => v.id !== next.id && v.name !== next.name)].slice(0, 16);
  if (import.meta.client) localStorage.setItem(SAVED_VIEWS_KEY, JSON.stringify(merged));
  return next;
}

export function removeFinanceSavedView(id: string) {
  const merged = loadFinanceSavedViews().filter((v) => v.id !== id);
  if (import.meta.client) localStorage.setItem(SAVED_VIEWS_KEY, JSON.stringify(merged));
  return merged;
}
